import ExcelJS from 'exceljs';
import { chunkWeeklyGuideTermWeekBlocks } from './weeklyLearningGuideA4Layout';
import { appendWeeklyGuideNeisLegendToSheet } from './weeklyLearningGuideNeisLegendExport';
import { subjectTrackTintArgbOnWhite } from './subjectTrackTint';
import { resolveWeeklyGuideNeisCellText } from './weeklyLearningGuideNeisCellStyle';

export type WeeklyLearningGuideExportColumn = {
  weekdayLabel: string;
  dateHeader: string;
};

export type WeeklyLearningGuideNeisVsBaseKind = 'match-base' | 'diff-track' | 'dedicated-mismatch';

export type WeeklyLearningGuideExportCell = {
  subject: string;
  /** 카드형 PDF 헤더: 편제명(없으면 시간표명) */
  subjectTitle?: string;
  /** 카드형 PDF 헤더: 편제명과 다를 때의 시간표명 */
  subjectSubtitle?: string;
  /** 카드형 PDF: 평가 상태 코드(1 계획 · 2 진행 · 3 완료)와 표시 문구 */
  evalCode?: '1' | '2' | '3';
  evalLabel?: string;
  subjectBgHex?: string;
  unit: string;
  learning: string;
  page: string;
  material: string;
  neisTrackName?: string;
  neisDedicated?: boolean;
  neisUnregisteredTimetableText?: string;
  neisVsBase?: WeeklyLearningGuideNeisVsBaseKind;
  neisShortIncreaseHighlight?: boolean;
  slotSurface: 'class' | 'holiday' | 'activity' | 'calendar-non-class' | 'non-class';
};

export type WeeklyLearningGuideExportPeriod = {
  label: string;
  cells: WeeklyLearningGuideExportCell[];
};

/** NEIS 학기 전체 내보내기: 주별 시간표 블록 */
export type WeeklyLearningGuideExportWeekBlock = {
  titleLine1: string;
  titleLine2: string;
  columns: WeeklyLearningGuideExportColumn[];
  periods: WeeklyLearningGuideExportPeriod[];
  noticeText: string;
};

export type WeeklyLearningGuideExportPayload = {
  titleLine1: string;
  titleLine2: string;
  columns: WeeklyLearningGuideExportColumn[];
  periods: WeeklyLearningGuideExportPeriod[];
  noticeText: string;
  neisMode: boolean;
  /** NEIS 반별시간표 ON 시 학기 전체 주차(첫 주~마지막 주) */
  termWeekBlocks?: WeeklyLearningGuideExportWeekBlock[];
};

const PERIOD_ROW_HEIGHT = {
  subject: 18,
  unit: 18,
  learning: 54,
  page: 18,
  material: 18,
} as const;

const LEARNING_CELL_FONT_SIZE = 10;

const NOTICE_MIN_ROW_HEIGHT = 72;

const EXCEL_COLORS = {
  headerBg: 'FFE8E8EB',
  unitBg: 'FFE3DDD7',
  activityBg: 'FFF5F0E6',
  holidayBg: 'FFF8D7D7',
  nonClassBg: 'FFF0F0F0',
  defaultText: 'FF111111',
  border: 'FF000000',
} as const;

const EXCEL_NEIS_COLORS = {
  matchBase: 'FFFFFFFF',
  diffTrack: 'FFE2E2EA',
  dedicatedMismatch: 'FFFFF6CC',
  shortEdge: 'FF28712D',
  unregisteredText: 'FFD32F2F',
} as const;

function solidCellBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: EXCEL_COLORS.border } };
  return { top: { ...side }, left: { ...side }, bottom: { ...side }, right: { ...side } };
}

function solidFill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

function hexToArgb(hex: string | undefined, fallback: string = EXCEL_COLORS.defaultText): string {
  const trimmed = (hex ?? '').trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(trimmed)) return fallback;
  return `FF${trimmed.toUpperCase()}`;
}

function styleHeaderCell(cell: ExcelJS.Cell): void {
  cell.border = solidCellBorder();
  cell.fill = solidFill(EXCEL_COLORS.headerBg);
  cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
}

function styleBodyCell(cell: ExcelJS.Cell, align: 'left' | 'center' | 'top' = 'center'): void {
  cell.border = solidCellBorder();
  cell.font = { color: { argb: EXCEL_COLORS.defaultText } };
  cell.alignment = {
    vertical: align === 'top' ? 'top' : 'middle',
    horizontal: align === 'left' || align === 'top' ? 'left' : 'center',
    wrapText: true,
  };
}

/** 평가 상태 글자색: 화면 칩 색 계열(완료는 읽히도록 진한 회색) */
const EVAL_TEXT_ARGB: Record<'1' | '2' | '3', string> = {
  '1': 'FFFF376E',
  '2': 'FF2A9D86',
  '3': 'FF7A7A7A',
};

function applySurfaceFill(cell: ExcelJS.Cell, surface: WeeklyLearningGuideExportCell['slotSurface']): void {
  if (surface === 'holiday') {
    cell.fill = solidFill(EXCEL_COLORS.holidayBg);
    return;
  }
  if (surface === 'activity') {
    cell.fill = solidFill(EXCEL_COLORS.activityBg);
    return;
  }
  if (surface === 'non-class' || surface === 'calendar-non-class') {
    cell.fill = solidFill(EXCEL_COLORS.nonClassBg);
  }
}

function applyNeisExportClassFill(cell: ExcelJS.Cell, cellData: WeeklyLearningGuideExportCell): void {
  applySurfaceFill(cell, cellData.slotSurface);
  if (cellData.slotSurface !== 'class') return;
  if (cellData.neisVsBase === 'match-base') {
    cell.fill = solidFill(EXCEL_NEIS_COLORS.matchBase);
    return;
  }
  if (cellData.neisVsBase === 'diff-track') {
    cell.fill = solidFill(EXCEL_NEIS_COLORS.diffTrack);
    return;
  }
  if (cellData.neisVsBase === 'dedicated-mismatch') {
    cell.fill = solidFill(EXCEL_NEIS_COLORS.dedicatedMismatch);
  }
}

function applyNeisShortEdgeBorder(
  cell: ExcelJS.Cell,
  periods: WeeklyLearningGuideExportPeriod[],
  periodIdx: number,
  colIdx: number
): void {
  const cellData = periods[periodIdx]!.cells[colIdx]!;
  if (!cellData.neisShortIncreaseHighlight) return;

  const topNeighbor =
    periodIdx > 0 && !!periods[periodIdx - 1]!.cells[colIdx]?.neisShortIncreaseHighlight;
  const bottomNeighbor =
    periodIdx < periods.length - 1 &&
    !!periods[periodIdx + 1]!.cells[colIdx]?.neisShortIncreaseHighlight;
  const rowCells = periods[periodIdx]!.cells;
  const leftNeighbor = colIdx > 0 && !!rowCells[colIdx - 1]?.neisShortIncreaseHighlight;
  const rightNeighbor = colIdx < rowCells.length - 1 && !!rowCells[colIdx + 1]?.neisShortIncreaseHighlight;

  const baseBorder = solidCellBorder();
  const thick: ExcelJS.Border = { style: 'medium', color: { argb: EXCEL_NEIS_COLORS.shortEdge } };
  cell.border = {
    top: !topNeighbor ? thick : baseBorder.top!,
    bottom: !bottomNeighbor ? thick : baseBorder.bottom!,
    left: !leftNeighbor ? thick : baseBorder.left!,
    right: !rightNeighbor ? thick : baseBorder.right!,
  };
}

function triggerBrowserDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Excel 열 번호(1-based) → 열 문자(A, B, …, AA) */
function columnIndexToLetter(columnIndex: number): string {
  let index = columnIndex;
  let letters = '';
  while (index > 0) {
    const remainder = (index - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    index = Math.floor((index - 1) / 26);
  }
  return letters;
}

/** 표 전체를 A4 가로 1페이지에 맞추는 인쇄 영역·페이지 설정 */
function applyWeeklyGuideA4SinglePagePrintSetup(
  sheet: ExcelJS.Worksheet,
  lastRow: number,
  lastColumn: number
): void {
  const lastColumnLetter = columnIndexToLetter(lastColumn);
  sheet.pageSetup = {
    paperSize: 9,
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 1,
    printArea: `A1:${lastColumnLetter}${lastRow}`,
    horizontalCentered: true,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.3,
      bottom: 0.3,
      header: 0.2,
      footer: 0.2,
    },
  };
}

/** 학기 전체(주별 다중 블록) — 가로 1페이지 폭, 세로는 필요한 만큼 페이지 분할 */
function applyWeeklyGuideA4MultiWeekPrintSetup(
  sheet: ExcelJS.Worksheet,
  lastRow: number,
  lastColumn: number
): void {
  const lastColumnLetter = columnIndexToLetter(lastColumn);
  sheet.pageSetup = {
    paperSize: 9,
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printArea: `A1:${lastColumnLetter}${lastRow}`,
    horizontalCentered: true,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.3,
      bottom: 0.3,
      header: 0.2,
      footer: 0.2,
    },
  };
}

function appendWeeklyGuideWeekBlockToSheet(
  sheet: ExcelJS.Worksheet,
  startRowIndex: number,
  block: WeeklyLearningGuideExportWeekBlock,
  neisMode: boolean,
  options?: { includeNotice?: boolean }
): number {
  const { titleLine1, titleLine2, columns, periods, noticeText } = block;
  const includeNotice = options?.includeNotice ?? true;
  const columnCount = 1 + columns.length;
  const hasSecondTitleLine = titleLine2.trim().length > 0;

  let rowIndex = startRowIndex;

  const titleRow1 = sheet.getRow(rowIndex);
  titleRow1.getCell(1).value = titleLine1;
  sheet.mergeCells(rowIndex, 1, rowIndex, columnCount);
  titleRow1.getCell(1).font = { bold: true, size: 14 };
  titleRow1.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  titleRow1.height = 22;
  rowIndex += 1;

  if (hasSecondTitleLine) {
    const titleRow2 = sheet.getRow(rowIndex);
    titleRow2.getCell(1).value = titleLine2;
    sheet.mergeCells(rowIndex, 1, rowIndex, columnCount);
    titleRow2.getCell(1).font = { bold: true, size: 12 };
    titleRow2.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    titleRow2.height = 20;
    rowIndex += 1;
  }

  const headerRow1Index = rowIndex;
  const headerRow2Index = rowIndex + 1;
  const headerRow1 = sheet.getRow(headerRow1Index);
  const headerRow2 = sheet.getRow(headerRow2Index);

  headerRow1.getCell(1).value = '구분';
  sheet.mergeCells(headerRow1Index, 1, headerRow2Index, 1);
  styleHeaderCell(headerRow1.getCell(1));

  columns.forEach((column, index) => {
    const col = index + 2;
    headerRow1.getCell(col).value = column.weekdayLabel;
    headerRow2.getCell(col).value = column.dateHeader;
    styleHeaderCell(headerRow1.getCell(col));
    styleHeaderCell(headerRow2.getCell(col));
  });

  headerRow1.height = 20;
  headerRow2.height = 20;
  rowIndex = headerRow2Index + 1;

  periods.forEach((period, periodIdx) => {
    if (neisMode) {
      const row = sheet.getRow(rowIndex);
      row.getCell(1).value = period.label;
      styleHeaderCell(row.getCell(1));
      period.cells.forEach((cellData, colIndex) => {
        const cell = row.getCell(colIndex + 2);
        const { text, unregistered, showDedicatedLabel } = resolveWeeklyGuideNeisCellText(cellData);
        if (showDedicatedLabel) {
          cell.value = {
            richText: [
              { text, font: { bold: true, size: 10, color: { argb: EXCEL_COLORS.defaultText } } },
              { text: '\n(전담)', font: { bold: false, size: 8, color: { argb: 'FF888888' } } },
            ],
          };
        } else {
          cell.value = text;
          cell.font = {
            bold: true,
            color: { argb: unregistered ? EXCEL_NEIS_COLORS.unregisteredText : EXCEL_COLORS.defaultText },
          };
        }
        styleBodyCell(cell, 'center');
        applyNeisExportClassFill(cell, cellData);
        applyNeisShortEdgeBorder(cell, periods, periodIdx, colIndex);
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });
      row.height = 28;
      rowIndex += 1;
      return;
    }

    const rowSpan = 5;
    const startRowIndexInner = rowIndex;
    const endRowIndex = rowIndex + rowSpan - 1;
    sheet.mergeCells(startRowIndexInner, 1, endRowIndex, 1);
    const periodTitleCell = sheet.getRow(startRowIndexInner).getCell(1);
    periodTitleCell.value = period.label;
    styleHeaderCell(periodTitleCell);

    const rowValues: Array<Array<{ value: string; kind: 'subject' | 'unit' | 'learning' | 'page' | 'material' }>> = [
      period.cells.map((cell) => ({ value: cell.subject, kind: 'subject' as const })),
      period.cells.map((cell) => ({
        value: cell.evalCode && cell.evalLabel ? `${cell.unit}${cell.unit ? '  ' : ''}[${cell.evalLabel}]` : cell.unit,
        kind: 'unit' as const,
      })),
      period.cells.map((cell) => ({ value: cell.learning, kind: 'learning' as const })),
      period.cells.map((cell) => ({ value: cell.page, kind: 'page' as const })),
      period.cells.map((cell) => ({ value: cell.material, kind: 'material' as const })),
    ];

    rowValues.forEach((rowCells, rowOffset) => {
      const row = sheet.getRow(startRowIndexInner + rowOffset);
      const rowKind = (['subject', 'unit', 'learning', 'page', 'material'] as const)[rowOffset]!;
      rowCells.forEach((entry, colIndex) => {
        const cellData = period.cells[colIndex]!;
        const cell = row.getCell(colIndex + 2);
        cell.value = entry.value;
        const align = entry.kind === 'learning' ? 'top' : 'center';
        styleBodyCell(cell, align);
        applySurfaceFill(cell, cellData.slotSurface);
        if (entry.kind === 'unit' && cellData.slotSurface === 'class') {
          cell.fill = solidFill(EXCEL_COLORS.unitBg);
          if (cellData.evalCode && cellData.evalLabel) {
            const baseFont = { ...(cell.font ?? {}) };
            cell.value = {
              richText: [
                ...(cellData.unit ? [{ text: `${cellData.unit}  `, font: baseFont }] : []),
                {
                  text: `[${cellData.evalLabel}]`,
                  font: { ...baseFont, bold: true, color: { argb: EVAL_TEXT_ARGB[cellData.evalCode] } },
                },
              ],
            };
          }
        }
        if (entry.kind === 'subject' && cellData.slotSurface === 'class' && cellData.subjectBgHex) {
          cell.fill = solidFill(hexToArgb(cellData.subjectBgHex, 'FFFFFFFF'));
          cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
        }
        if (
          (entry.kind === 'learning' || entry.kind === 'page' || entry.kind === 'material') &&
          cellData.slotSurface === 'class' &&
          cellData.subjectBgHex
        ) {
          cell.fill = solidFill(subjectTrackTintArgbOnWhite(cellData.subjectBgHex));
        }
        if (entry.kind === 'learning') {
          cell.font = { size: LEARNING_CELL_FONT_SIZE, color: { argb: EXCEL_COLORS.defaultText } };
        }
      });
      const contentHeight = entryRowHeight(
        rowCells.map((entry) => entry.value),
        rowKind === 'learning' ? LEARNING_CELL_FONT_SIZE : undefined
      );
      row.height =
        rowKind === 'learning'
          ? Math.max(PERIOD_ROW_HEIGHT.learning, contentHeight)
          : Math.max(PERIOD_ROW_HEIGHT[rowKind], contentHeight);
    });

    rowIndex = endRowIndex + 1;
  });

  if (includeNotice) {
    const noticeRow = sheet.getRow(rowIndex);
    noticeRow.getCell(1).value = '가정통신문';
    styleHeaderCell(noticeRow.getCell(1));
    noticeRow.getCell(1).alignment = { vertical: 'top', horizontal: 'center', wrapText: true };
    noticeRow.getCell(2).value = noticeText;
    sheet.mergeCells(rowIndex, 2, rowIndex, columnCount);
    styleBodyCell(noticeRow.getCell(2), 'top');
    noticeRow.height = Math.max(NOTICE_MIN_ROW_HEIGHT, entryRowHeight([noticeText]));
    rowIndex += 1;
  }

  return rowIndex;
}

export async function saveWeeklyLearningGuideAsExcel(
  payload: WeeklyLearningGuideExportPayload,
  fileName: string
): Promise<void> {
  const { titleLine1, titleLine2, columns, periods, noticeText, neisMode, termWeekBlocks } = payload;

  if (termWeekBlocks && termWeekBlocks.length > 0) {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('NEIS 반별시간표', {
      views: [{ state: 'frozen', ySplit: 3, xSplit: 1 }],
    });
    const maxColumnCount = Math.max(
      ...termWeekBlocks.map((block) => 1 + block.columns.length),
      2
    );
    const pages = chunkWeeklyGuideTermWeekBlocks(termWeekBlocks);
    let currentRowIndex = 1;
    pages.forEach((pageBlocks, pageIdx) => {
      if (pageIdx > 0) {
        sheet.getRow(currentRowIndex).addPageBreak();
      }
      currentRowIndex = appendWeeklyGuideNeisLegendToSheet(sheet, currentRowIndex, maxColumnCount);
      pageBlocks.forEach((block, index) => {
        currentRowIndex = appendWeeklyGuideWeekBlockToSheet(sheet, currentRowIndex, block, true, {
          includeNotice: false,
        });
        if (index < pageBlocks.length - 1) {
          currentRowIndex += 1;
        }
      });
    });
    sheet.getColumn(1).width = 10;
    for (let col = 2; col <= maxColumnCount; col += 1) {
      sheet.getColumn(col).width = 30;
    }
    applyWeeklyGuideA4MultiWeekPrintSetup(sheet, currentRowIndex - 1, maxColumnCount);
    const buffer = await workbook.xlsx.writeBuffer();
    triggerBrowserDownload(buffer, fileName);
    return;
  }

  const columnCount = 1 + columns.length;
  const hasSecondTitleLine = titleLine2.trim().length > 0;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('주간학습 안내', {
    views: [{ state: 'frozen', ySplit: hasSecondTitleLine ? 4 : 3, xSplit: 1 }],
  });

  let currentRowIndex = appendWeeklyGuideWeekBlockToSheet(sheet, 1, {
    titleLine1,
    titleLine2,
    columns,
    periods,
    noticeText,
  }, neisMode);

  sheet.getColumn(1).width = 10;
  for (let col = 2; col <= columnCount; col += 1) {
    sheet.getColumn(col).width = 30;
  }

  applyWeeklyGuideA4SinglePagePrintSetup(sheet, currentRowIndex - 1, columnCount);

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBrowserDownload(buffer, fileName);
}

function entryRowHeight(values: string[], fontSize?: number): number {
  const lineHeight = fontSize && fontSize <= 10 ? 13 : 14;
  const lines = values.reduce((max, value) => Math.max(max, value.split('\n').length), 1);
  return Math.min(120, Math.max(18, lines * lineHeight));
}
